import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UiButtonDirective } from '@template-ui/directives';
import type { WorkflowDefinitionFamilyVisibility } from './platform-workflow-definition-lifecycle.models';

export interface WorkflowFamilyVisibilityState {
  busy: boolean;
  blocked: boolean;
  retry: boolean;
  message: string | null;
}
export interface WorkflowFamilyVisibilityRequest {
  detail: WorkflowDefinitionFamilyVisibility;
  isHidden: boolean;
  context: string;
}

/** Shared visibility is registry metadata; this presentation never owns a draft. */
@Component({
  selector: 'phms-workflow-family-visibility',
  imports: [CommonModule, UiButtonDirective],
  template: `
    <div class="family-visibility" role="group" [attr.aria-label]="'Workflow actions: ' + workflowCode" [attr.data-family-visibility]="workflowCode">
      <span *ngIf="available && detail?.isAvailable && detail?.isHidden === true" data-family-hidden>Hidden for all authors</span>
      <span *ngIf="!busy && (!available || detail?.isAvailable === false)" role="status">Shared visibility is unavailable on this installation.</span>
      <span *ngIf="available && !detail && !busy && !message" role="status">Shared visibility has not been loaded.</span>
      <span *ngIf="busy" role="status">Updating shared visibility…</span>
      <span *ngIf="message" role="status">{{ message }}</span>
      <button *ngIf="canAuthor && detail as current" uiButton type="button" data-change-family-visibility
        [disabled]="!available || !current.isAvailable || current.isHidden === null || busy || blocked"
        [attr.aria-label]="(current.isHidden === null ? 'Change visibility: ' : current.isHidden ? 'Restore ' : 'Hide for all authors: ') + workflowCode"
        (click)="change(current)">{{ current.isHidden === null ? 'Change visibility' : current.isHidden ? 'Restore' : 'Hide for all authors' }}</button>
      <button *ngIf="canAuthor && !detail" uiButton type="button" disabled>Change visibility</button>
      <button *ngIf="retry" uiButton type="button" data-retry-family-visibility [disabled]="busy || !canAuthor || !available" (click)="retryRequested.emit()">Retry visibility change</button>
      <button uiButton type="button" data-refresh-family-visibility [disabled]="busy" (click)="refreshRequested.emit()">Refresh visibility</button>
    </div>`,
  styles: [`.family-visibility{display:flex;align-items:center;gap:.5rem;flex-wrap:wrap;min-width:0;overflow-wrap:anywhere}button{min-height:44px}`],
})
export class WorkflowFamilyVisibilityComponent {
  @Input() workflowCode = '';
  @Input() context = '';
  @Input() detail: WorkflowDefinitionFamilyVisibility | null = null;
  @Input() available = false;
  @Input() canAuthor = false;
  @Input() busy = false;
  @Input() blocked = false;
  @Input() retry = false;
  @Input() message: string | null = null;
  @Output() changeRequested = new EventEmitter<WorkflowFamilyVisibilityRequest>();
  @Output() refreshRequested = new EventEmitter<void>();
  @Output() retryRequested = new EventEmitter<void>();
  change(detail: WorkflowDefinitionFamilyVisibility): void {
    if (!this.canAuthor || this.busy || this.blocked || !this.available || detail !== this.detail || !detail.isAvailable ||
      typeof detail.isHidden !== 'boolean' || !Number.isInteger(detail.revision) || !detail.eTag) return;
    this.changeRequested.emit({ detail, isHidden: !detail.isHidden, context: this.context });
  }
}
